import type { FC, MouseEventHandler } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { IconButton, Stack, Typography, CircularProgress } from "@mui/material";
import { useShallowPickSelector } from "store/useSelector";
import { BottomSheet } from "components/atoms/BottomSheet";
import { appActions } from "store/app/app.slice";
import {
  useAddToLikedTracksMutation,
  useGetLikedTracksQuery,
  useRemoveFromLikedTracksMutation,
  useRemoveFromPlaylistMutation,
} from "store/current-user/current-user.api";
import { archiveTrack, removeArchivedTrack } from "store/archive/archive.thunk";
import { currentUserActions } from "store/current-user/current-user.slice";
import { ReactComponent as EditSVG } from "assets/images/edit-outline.svg";
import { ReactComponent as StarSVG } from "assets/images/star-outline-white.svg";
import { ReactComponent as ShareSVG } from "assets/images/share.svg";
import { ReactComponent as LogoutSVG } from "assets/images/logout-outline.svg";
import { ReactComponent as ShuffleSVG } from "assets/images/shuffle.svg";
import { ReactComponent as RepeatSVG } from "assets/images/repeat.svg";
import { ReactComponent as RepeatOneSVG } from "assets/images/repeat-one.svg";
import { ReactComponent as PlaylistSVG } from "assets/images/queue.svg";
import { ReactComponent as DeleteSVG } from "assets/images/delete-outline.svg";
import { ReactComponent as AlbumSVG } from "assets/images/album-outline.svg";
import { ReactComponent as ArtistSVG } from "assets/images/profile-outline.svg";
import { ReactComponent as DownloadSVG } from "assets/images/download.svg";
import { ReactComponent as HeartOutlineSVG } from "assets/images/heart-outline.svg";
import { ReactComponent as HeartSVG } from "assets/images/heart.svg";
import { playerActions } from "store/player/player.slice";
import type { RepeatMode } from "store/player/player.slice";
import { TrackItem } from "./TrackItem";

const RepeatIcons = {
  all: <RepeatSVG />,
  one: <RepeatOneSVG />,
  off: <RepeatSVG opacity={0.23} />,
};

const repeatModeNextState: { [K in RepeatMode]: RepeatMode } = {
  one: "off",
  all: "one",
  off: "all",
};

export const TrackMenu: FC = () => {
  const { trackMenu } = useShallowPickSelector("app", ["trackMenu"]);
  const { user, auth } = useShallowPickSelector("currentUser", [
    "user",
    "auth",
  ]);
  const { isShuffle, repeatMode } = useShallowPickSelector("player", [
    "isShuffle",
    "repeatMode",
  ]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const _location = useLocation();

  const [addToLikedTracks] = useAddToLikedTracksMutation();
  const [removeFromLikedTracks] = useRemoveFromLikedTracksMutation();
  const { data: likedTracks } = useGetLikedTracksQuery(
    { userId: user?.id! },
    { skip: !user || !auth || !trackMenu.isOpen }
  );
  const [removeFromPlaylist] = useRemoveFromPlaylistMutation();

  const isTrackLiked =
    likedTracks &&
    trackMenu.track &&
    likedTracks.findIndex((t) => t.id === trackMenu.track!.id) !== -1;

  const onCloseTrackMenu = () => {
    dispatch(
      appActions.setTrackMenu({
        isOpen: false,
        track: trackMenu.track,
        isOnPlayerScreen: false,
        isDeletable: false,
        playlist: null,
      })
    );
  };

  const onClickRepeat: MouseEventHandler = () => {
    dispatch(playerActions.setRepeatMode(repeatModeNextState[repeatMode]));
  };

  const onClickShuffle: MouseEventHandler = () => {
    dispatch(playerActions.setIsShuffle(!isShuffle));
  };

  const onClickAddToPlaylist: MouseEventHandler = () => {
    if (trackMenu.track) {
      dispatch(
        appActions.setAddToPlaylistMenu({
          isOpen: true,
          track: trackMenu.track,
        })
      );
    }
  };

  const onClickRemoveFromPlaylist: MouseEventHandler = () => {
    if (!user || !trackMenu.track || !trackMenu.playlist) return;
    removeFromPlaylist({
      playlistId: trackMenu.playlist.id,
      body: { ids: [trackMenu.track.id] },
    });
    onCloseTrackMenu();
  };

  const onClickNavigateToAlbum: MouseEventHandler = () => {
    if (trackMenu?.track?.albumId) {
      navigate(`/albums/${trackMenu.track.albumId}`);
      onCloseTrackMenu();
    }
  };

  const onClickNavigateToArtist: MouseEventHandler = () => {
    if (trackMenu?.track?.artistId) {
      navigate(`/artists/${trackMenu.track.artistId}`);
      onCloseTrackMenu();
    }
  };

  const { tracks: archivedTracks } = useShallowPickSelector("archive", [
    "tracks",
  ]);

  useShallowPickSelector("player", ["currentTrack"]);

  const archivedTrack = trackMenu.track
    ? archivedTracks.find(
        (t) => trackMenu.track && t.id === trackMenu.track.id
      ) || null
    : null;

  const onClickDownload: MouseEventHandler = () => {
    if (!trackMenu.track) return;
    if (archivedTrack) {
      dispatch(removeArchivedTrack({ trackId: trackMenu.track.id }));
    } else {
      dispatch(archiveTrack({ track: trackMenu.track }));
    }
    onCloseTrackMenu();
  };

  const onClickLike: MouseEventHandler = () => {
    if (!trackMenu.track || !user) return;
    if (isTrackLiked) {
      removeFromLikedTracks({
        body: { ids: [trackMenu.track.id] },
        userId: user.id,
      });
    } else {
      addToLikedTracks({
        body: { ids: [trackMenu.track.id] },
        userId: user.id,
        tracks: [trackMenu.track],
      });
    }
  };

  const onClickShare: MouseEventHandler = () => {
    //trackMenu.track
  };

  return (
    <BottomSheet
      Title={
        trackMenu.isOnPlayerScreen ? (
          <Stack direction={"row"} m={3} ml={2}>
            <IconButton onClick={onClickRepeat}>
              {RepeatIcons[repeatMode]}
            </IconButton>
            <IconButton onClick={onClickShuffle}>
              {<ShuffleSVG opacity={isShuffle ? 1 : 0.23} />}
            </IconButton>
            <Typography textAlign={"right"} fontWeight={700} flex={1}>
              گزینه‌ها
            </Typography>
          </Stack>
        ) : (
          <Stack
            m={1}
            pb={2}
            borderBottom={"1.5px solid rgba(255,255,255,0.05)"}
            sx={{ "& *": { cursor: "auto" } }}
          >
            <TrackItem track={trackMenu.track!} />
          </Stack>
        )
      }
      isOpen={trackMenu.isOpen}
      onClose={onCloseTrackMenu}
      items={[
        {
          text: "افزودن به پلی‌لیست",
          icon: <PlaylistSVG />,
          onClick: onClickAddToPlaylist,
        },
        trackMenu.isDeletable
          ? {
              text: "حذف از پلی‌لیست",
              icon: <DeleteSVG />,
              onClick: onClickRemoveFromPlaylist,
            }
          : null,
        trackMenu?.track?.albumId
          ? {
              text: "مشاهدهٔ آلبوم",
              icon: <AlbumSVG />,
              onClick: onClickNavigateToAlbum,
            }
          : null,
        trackMenu?.track?.artistId
          ? {
              text: "مشاهدهٔ هنرمند",
              icon: <ArtistSVG />,
              onClick: onClickNavigateToArtist,
            }
          : null,
        {
          text:
            archivedTrack &&
            archivedTrack.downloadProgress &&
            archivedTrack.downloadProgress === 1
              ? "حذف از آرشیو"
              : "دانلود",
          icon:
            archivedTrack && archivedTrack.downloadProgress ? (
              archivedTrack.downloadProgress < 1 ? (
                <CircularProgress
                  value={archivedTrack.downloadProgress * 100}
                  size={24}
                  variant={"determinate"}
                />
              ) : (
                <DeleteSVG opacity={0.4} />
              )
            ) : (
              <DownloadSVG opacity={0.4} />
            ),
          onClick: onClickDownload,
        },
        {
          text: isTrackLiked ? "حذف از مورد علاقه" : "افزودن به مورد علاقه",
          icon: isTrackLiked ? <HeartSVG /> : <HeartOutlineSVG />,
          onClick: onClickLike,
        },
        /*
        {
          text: "اشتراک‌گذاری",
          icon: <ShareSVG />,
          onClick: onClickShare,
        },
        */
      ]}
    />
  );
};
